import {
  createStyles,
  InputAdornment,
  TextField,
  Typography,
  withStyles,
  type WithStyles,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import React, { type ChangeEvent } from "react";
import type {
  EnrichedAnimalCategory,
  LivestockInfoFromFarm,
} from "shared/types";
import {
  getDateForGoalWeight,
  parseWeightInput,
} from "../../../utils/lots.utils";

type GoalWeightColumnCellProps = {
  enrichedAnimalCategories: EnrichedAnimalCategory[];
  error: string | null | undefined;
  hasChangedField: boolean;
  onBlur: (row: LivestockInfoFromFarm) => void;
  onChange: (weight: number | null, row: LivestockInfoFromFarm) => void;
  row: LivestockInfoFromFarm;
  value: number | null;
} & WithStyles<typeof styles>;

function GoalWeightColumnCell({
  classes,
  enrichedAnimalCategories,
  error,
  hasChangedField,
  onBlur,
  onChange,
  row,
  value,
}: GoalWeightColumnCellProps) {
  const { t } = useTranslations();

  const parsedValue = value ? value.toString() : "";

  const dateForProjection = getDateForGoalWeight(
    enrichedAnimalCategories,
    row,
    value
  );

  const reachDateMessage =
    dateForProjection != null && !hasChangedField
      ? t("livestock:goal-weight-reach-projection", { date: dateForProjection })
      : null;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const weight = parseWeightInput(event.target.value);
    onChange(weight, row);
  };

  const handleBlur = () => {
    onBlur(row);
  };

  return (
    <div className={classes.root}>
      <TextField
        aria-invalid={!!error}
        className={classes.input}
        data-testid="lot-goal-weight-input"
        error={!!error}
        helperText={error}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {t("common:measure.KG")}
            </InputAdornment>
          ),
        }}
        inputProps={{ min: 0 }}
        onBlur={handleBlur}
        onChange={handleChange}
        placeholder={t("livestock:weight")}
        type="number"
        value={parsedValue}
      />

      <Typography
        className={classes.text}
        component="span"
        data-testid="lot-goal-weight-text"
      >
        {value ?? "-"}
      </Typography>

      {reachDateMessage != null && (
        <span
          className={classes.reachDateMessage}
          data-testid="reach-date-message"
        >
          {reachDateMessage}
        </span>
      )}
    </div>
  );
}

const styles = () =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      height: 84,
      justifyContent: "center",
      position: "relative",
      width: "100px",
      "@media print": {
        width: "min-content",
      },
    },
    input: {
      marginTop: "14px",
      "@media print": {
        display: "none",
      },
    },
    text: {
      display: "none",
      "@media print": {
        display: "block",
        fontSize: "inherit",
      },
    },
    reachDateMessage: {
      width: 170,
      height: 14,
      fontSize: 12,
      position: "absolute",
      color: "#32a07d",
      bottom: 2,
    },
  });

export default withStyles(styles)(GoalWeightColumnCell);
